import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { APIProvider, Map as GMap, AdvancedMarker, Pin, useMap } from "@vis.gl/react-google-maps";
import { useOrder, STATUS_LABEL, STATUS_PILL, PAYMENT_LABEL } from "@/lib/orders";
import { supabase } from "@/lib/supabase";
import { useTourLivePosition } from "@/lib/tours";

const HOF = { lat: 52.0392, lng: 8.1503 };
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY ?? "";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

export default function BestelldetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrder(id);
  const productIds = order?.items?.map((i) => i.product_id) ?? [];
  const { data: products = [] } = useQuery({
    queryKey: ["product-names", productIds.join(",")],
    enabled: productIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("products").select("id,name,unit").in("id", productIds);
      return data ?? [];
    },
  });
  const nameById = new Map(products.map((p) => [p.id, p]));

  if (isLoading) return <div className="card">Lade…</div>;
  if (!order) return (
    <div className="card text-center text-hof-600">
      Bestellung nicht gefunden. <Link to="/shop/bestellungen" className="text-wiese-700 underline">Übersicht</Link>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <Link to="/shop/bestellungen" className="text-sm text-hof-600 hover:text-hof-900">← zu meinen Bestellungen</Link>
        <h1 className="text-3xl font-brand font-bold text-wappen-700">Bestellung {order.order_number ?? ""}</h1>
        <div className="mt-2 flex items-center gap-2">
          <span className={STATUS_PILL[order.status]}>{STATUS_LABEL[order.status]}</span>
          <span className="text-hof-500 text-sm">{PAYMENT_LABEL[order.payment_method]}</span>
        </div>
      </div>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-2">Lieferung</h2>
        <div className="text-hof-900">{dfmt.format(new Date(order.delivery_date))}</div>
        {order.address && (
          <div className="text-hof-700 mt-1">{order.address.street}, {order.address.zip} {order.address.city}</div>
        )}
        {order.notes && <div className="text-sm text-hof-500 mt-2">Hinweis: {order.notes}</div>}
      </section>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-2">Artikel</h2>
        <div className="divide-y divide-hof-100">
          {(order.items ?? []).map((i) => {
            const p = nameById.get(i.product_id);
            return (
              <div key={i.id} className="py-2 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-hof-900">{p?.name ?? "—"}</div>
                  <div className="text-xs text-hof-500">{i.qty} × {fmt.format(i.unit_price_cents / 100)}</div>
                </div>
                <div className="font-medium">{fmt.format((i.qty * i.unit_price_cents) / 100)}</div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-hof-200 mt-3 pt-3 flex justify-between font-bold text-xl">
          <span>Gesamt</span>
          <span className="text-wiese-700">{fmt.format(order.total_cents / 100)}</span>
        </div>
      </section>

      {(order.status === "placed" || order.status === "planned") && (
        <p className="text-sm text-hof-600 text-center">Live-Tracking erscheint hier, sobald die Lieferung startet.</p>
      )}

      {order.status === "in_delivery" && order.id && <LiveTracking orderId={order.id} />}
    </div>
  );
}

function LiveTracking({ orderId }: { orderId: string }) {
  // Tour-ID über delivery_stops ermitteln (Kunde hat dafür eine Select-Policy)
  const { data: stop } = useQuery({
    queryKey: ["stop-for-order", orderId],
    queryFn: async () => {
      const { data } = await supabase
        .from("delivery_stops")
        .select("id, tour_id, seq, eta_at")
        .eq("order_id", orderId)
        .maybeSingle();
      return data;
    },
    refetchInterval: 30_000,
  });

  const tourId = stop?.tour_id;
  const { data: pos } = useTourLivePosition(tourId);

  if (!tourId) return null;

  return (
    <section className="card">
      <h2 className="font-semibold text-hof-800 mb-2">🚐 Live-Tracking</h2>
      {stop?.eta_at && (
        <p className="text-hof-700 mb-3">
          Voraussichtliche Ankunft: <span className="font-medium">
            {new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(stop.eta_at))}
          </span> (Stop {stop.seq})
        </p>
      )}
      <div className="h-64 rounded-xl overflow-hidden">
        <APIProvider apiKey={MAPS_KEY} libraries={["marker"]}>
          <GMap mapId="sirges-live" defaultCenter={pos ?? HOF} defaultZoom={11} gestureHandling="cooperative">
            <FitToPoints points={[pos, HOF].filter(Boolean) as Array<{lat:number;lng:number}>} />
            <AdvancedMarker position={HOF}>
              <Pin background="#5a8b35" borderColor="#365520" glyphColor="#fff" />
            </AdvancedMarker>
            {pos && (
              <AdvancedMarker position={{ lat: pos.lat, lng: pos.lng }}>
                <Pin background="#3b82f6" borderColor="#1e40af" glyphColor="#fff">🚐</Pin>
              </AdvancedMarker>
            )}
          </GMap>
        </APIProvider>
      </div>
      {pos && (
        <p className="text-xs text-hof-500 mt-2 text-center">
          Letzte Position: {new Date(pos.ts).toLocaleTimeString("de-DE")}
        </p>
      )}
    </section>
  );
}

function FitToPoints({ points }: { points: Array<{ lat: number; lng: number }> }) {
  const map = useMap();
  useEffect(() => {
    if (!map || points.length === 0 || typeof google === "undefined") return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 80);
  }, [map, points]);
  return null;
}
